import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReportsApi } from './api';
import type { CreateDashboardDto, ReportFilters, Report, ReportAccess } from './types';

const QUERY_KEYS = {
  all: ['reports'] as const,
  // Dashboard keys
  list: (chatbotId: string) => [...QUERY_KEYS.all, 'list', chatbotId] as const,
  detail: (chatbotId: string, dashboardId?: string, filters?: ReportFilters) => [...QUERY_KEYS.all, 'detail', chatbotId, dashboardId, filters] as const,
  // Report management keys
  orgReports: (orgId: string) => [...QUERY_KEYS.all, 'org', orgId] as const,
  reportAccess: (reportId: string) => [...QUERY_KEYS.all, 'access', reportId] as const,
};
// ===== Report Management Hooks =====

/**
 * Hook to fetch all reports for an organization
 */
export const useReports = (orgId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.orgReports(orgId),
    queryFn: () => ReportsApi.getReports(orgId),
    enabled: !!orgId,
  });
};

/**
 * Hook to fetch report access list
 */
export const useReportAccess = (reportId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.reportAccess(reportId),
    queryFn: () => ReportsApi.getReportAccess(reportId),
    enabled: !!reportId,
  });
};

/**
 * Hook to share report with team members
 */
export const useShareReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, access }: { reportId: string; access: ReportAccess[] }) =>
      ReportsApi.shareReport(reportId, access),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reportAccess(reportId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

/**
 * Hook to revoke report access from a user
 */
export const useRevokeReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, access }: { reportId: string; access: ReportAccess[] }) =>
      ReportsApi.revokeAccess(reportId, access),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reportAccess(reportId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

/**
 * Hook to create a new report
 */
export const useCreateReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: Omit<Report, 'id' | 'org_id' | 'created_at' | 'updated_at'> }) =>
      ReportsApi.createReport(orgId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orgReports(orgId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

/**
 * Hook to update an existing report
 */
export const useUpdateReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: Partial<Omit<Report, 'id' | 'org_id' | 'created_at'>> }) =>
      ReportsApi.updateReport(reportId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

/**
 * Hook to delete a report
 */
export const useDeleteReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => ReportsApi.deleteReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

